<template>
  <transition name="rm-modal-fade">
    <div class="rm-detail-overlay" v-if="open" @click.self="$emit('close')">
      <div class="rm-detail-modal">

        <!-- ── Header ── -->
        <div class="rm-detail-header">
          <div class="rm-detail-header-left">
            <i class="fa-solid fa-file-contract"></i>
            <div>
              <h2 class="rm-detail-title">Reverse Mortgage Request</h2>
              <p class="rm-detail-subtitle">{{ item.bwrfullname || '—' }}</p>
            </div>
          </div>
          <button class="rm-detail-close" @click="$emit('close')">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- ── Submitted meta bar ── -->
        <div class="rm-detail-meta-bar">
          <span class="rm-detail-meta-item">
            <i class="fa-regular fa-clock"></i>
            Submitted {{ item.submittedat || '—' }}
          </span>
          <span class="rm-detail-meta-item">
            <i class="fa-regular fa-user-tie"></i>
            {{ item.loname || '—' }}
          </span>
          <span class="rm-detail-meta-item" v-if="item.reverseprogram">
            <i class="fa-regular fa-tag"></i>
            {{ item.reverseprogram }}
          </span>
          <span class="rm-detail-meta-item" v-if="item.purposeofloan">
            <i class="fa-regular fa-bullseye-arrow"></i>
            {{ item.purposeofloan }}
          </span>
        </div>

        <!-- ── Scrollable body ── -->
        <div class="rm-detail-body">

          <!-- LO Information -->
          <div class="rm-detail-section">
            <div class="rm-detail-section-header">
              <i class="fa-solid fa-id-card"></i>
              <span>Loan Officer</span>
            </div>
            <div class="rm-detail-grid">
              <div class="rm-detail-field">
                <span class="rm-detail-label">Name</span>
                <span class="rm-detail-value">{{ item.loname || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">NMLS ID</span>
                <span class="rm-detail-value">{{ item.lonmlsid || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Company</span>
                <span class="rm-detail-value">{{ item.locompanyname || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Email</span>
                <span class="rm-detail-value">{{ item.loemail || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Cell Phone</span>
                <span class="rm-detail-value">{{ item.locellphone || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Office Phone</span>
                <span class="rm-detail-value">{{ item.loofficephonephone || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Program</span>
                <span class="rm-detail-value">{{ item.reverseprogram || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Purpose</span>
                <span class="rm-detail-value">{{ item.purposeofloan || '—' }}</span>
              </div>
              <div class="rm-detail-field rm-detail-field--full">
                <span class="rm-detail-label">Goal of Program</span>
                <span class="rm-detail-value">{{ item.goalofprogram || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Property Type</span>
                <span class="rm-detail-value">{{ item.propertytype || '—' }}</span>
              </div>
            </div>
          </div>


          <!-- Borrower -->
          <div class="rm-detail-section">
            <div class="rm-detail-section-header">
              <i class="fa-solid fa-person"></i>
              <span>Borrower</span>
            </div>
            <div class="rm-detail-grid">
              <div class="rm-detail-field">
                <span class="rm-detail-label">Full Name</span>
                <span class="rm-detail-value">{{ item.bwrfullname || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Gender</span>
                <span class="rm-detail-value rm-detail-value--cap">{{ item.bwrgender || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Date of Birth</span>
                <span class="rm-detail-value">{{ formatDob(item.bwrdob) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Phone</span>
                <span class="rm-detail-value">{{ item.bwrphone || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Email</span>
                <span class="rm-detail-value">{{ item.bwremail || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">SSN</span>
                <span class="rm-detail-value rm-detail-value--ssn">{{ formatSsn(item.bwrssn) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Monthly Income</span>
                <span class="rm-detail-value">{{ item.bwrmonthlyincome || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Assets</span>
                <span class="rm-detail-value">{{ item.bwrassets || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Marital Status</span>
                <span class="rm-detail-value">{{ item.bwrmaritalstatus || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Ethnicity</span>
                <span class="rm-detail-value">{{ item.bwrethnicity || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Race</span>
                <span class="rm-detail-value">{{ item.bwrrace || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">US Citizen</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwruscitizen)]">{{ boolLabel(item.bwruscitizen) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Perm Resident</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrpermresident)]">{{ boolLabel(item.bwrpermresident) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Buy Financial Product</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrbuyfinancial)]">{{ boolLabel(item.bwrbuyfinancial) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Existing FHA</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrexistingfha)]">{{ boolLabel(item.bwrexistingfha) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Power of Attorney</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrpoa)]">{{ boolLabel(item.bwrpoa) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Bankruptcy</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrbankruptcy)]">{{ boolLabel(item.bwrbankruptcy) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Judgments</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrjudgments)]">{{ boolLabel(item.bwrjudgments) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Federal Debt</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrfederaldebt)]">{{ boolLabel(item.bwrfederaldebt) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Lawsuit</span>
                <span :class="['rm-detail-badge', boolBadge(item.bwrlawsuit)]">{{ boolLabel(item.bwrlawsuit) }}</span>
              </div>
            </div>
          </div>

          <!-- Co-Borrower -->
          <div class="rm-detail-section" v-if="item.cobwrfullname">
            <div class="rm-detail-section-header">
              <i class="fa-solid fa-people-group"></i>
              <span>Co-Borrower / NBS</span>
            </div>
            <div class="rm-detail-grid">
              <div class="rm-detail-field">
                <span class="rm-detail-label">Full Name</span>
                <span class="rm-detail-value">{{ item.cobwrfullname || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Gender</span>
                <span class="rm-detail-value rm-detail-value--cap">{{ item.cobwrgender || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Date of Birth</span>
                <span class="rm-detail-value">{{ formatDob(item.cobwrdob) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Phone</span>
                <span class="rm-detail-value">{{ item.cobwrphone || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Email</span>
                <span class="rm-detail-value">{{ item.cobwremail || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">SSN</span>
                <span class="rm-detail-value rm-detail-value--ssn">{{ formatSsn(item.cobwrssn) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Monthly Income</span>
                <span class="rm-detail-value">{{ item.cobwrmonthlyincome || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Assets</span>
                <span class="rm-detail-value">{{ item.cobwrassets || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Marital Status</span>
                <span class="rm-detail-value">{{ item.cobwrmaritalstatus || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">US Citizen</span>
                <span :class="['rm-detail-badge', boolBadge(item.cobwruscitizen)]">{{ boolLabel(item.cobwruscitizen) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Perm Resident</span>
                <span :class="['rm-detail-badge', boolBadge(item.cobwrpermresident)]">{{ boolLabel(item.cobwrpermresident) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Bankruptcy</span>
                <span :class="['rm-detail-badge', boolBadge(item.cobwrbankruptcy)]">{{ boolLabel(item.cobwrbankruptcy) }}</span>
              </div>
            </div>
          </div>

          <div class="rm-detail-section rm-detail-section--no-cobwr" v-else>
            <div class="rm-detail-section-header">
              <i class="fa-solid fa-people-group"></i>
              <span>Co-Borrower / NBS</span>
            </div>
            <p class="rm-detail-empty">No co-borrower on this application.</p>
          </div>

          <!-- Property -->
          <div class="rm-detail-section">
            <div class="rm-detail-section-header">
              <i class="fa-solid fa-house"></i>
              <span>Property</span>
            </div>
            <div class="rm-detail-grid">
              <div class="rm-detail-field rm-detail-field--full">
                <span class="rm-detail-label">Street Address</span>
                <span class="rm-detail-value">{{ item.propaddress || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">City</span>
                <span class="rm-detail-value">{{ item.propcity || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">State</span>
                <span class="rm-detail-value">{{ item.propstate || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">ZIP</span>
                <span class="rm-detail-value">{{ item.propzip || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Year Built</span>
                <span class="rm-detail-value">{{ item.propyearbuilt || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Est. Value</span>
                <span class="rm-detail-value">{{ item.propestvalue || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Sq Ft</span>
                <span class="rm-detail-value">{{ item.propsqft || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Mortgage Payoff</span>
                <span class="rm-detail-value">{{ item.propmortgagepayoff || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Tax / Mo</span>
                <span class="rm-detail-value">{{ item.proptaxmo || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">HOI / Mo</span>
                <span class="rm-detail-value">{{ item.prophoimo || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">HOA / Mo</span>
                <span class="rm-detail-value">{{ item.prophoamo || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Min Debt / Mo</span>
                <span class="rm-detail-value">{{ item.propmindebtmo || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Years at Address</span>
                <span class="rm-detail-value">{{ item.propyearsataddress || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Adults 18+ in Home</span>
                <span class="rm-detail-value">{{ item.propadultsinhome ?? '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Add'l Lien Payoff</span>
                <span class="rm-detail-value">{{ item.propaddllienpayoff || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Primary Residence</span>
                <span :class="['rm-detail-badge', boolBadge(item.propprimaryres)]">{{ boolLabel(item.propprimaryres) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">In Trust</span>
                <span :class="['rm-detail-badge', boolBadge(item.propintrust)]">{{ boolLabel(item.propintrust) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Refi Cash-Out</span>
                <span :class="['rm-detail-badge', boolBadge(item.propreficashout)]">{{ boolLabel(item.propreficashout) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Owns Additional</span>
                <span :class="['rm-detail-badge', boolBadge(item.propownsaddl)]">{{ boolLabel(item.propownsaddl) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Taxes Current</span>
                <span :class="['rm-detail-badge', boolBadge(item.proptaxescurrent)]">{{ boolLabel(item.proptaxescurrent) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">HOI Current</span>
                <span :class="['rm-detail-badge', boolBadge(item.prophoicurrent)]">{{ boolLabel(item.prophoicurrent) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">HOA Current</span>
                <span :class="['rm-detail-badge', boolBadge(item.prophoacurrent)]">{{ boolLabel(item.prophoacurrent) }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Solar Lease</span>
                <span :class="['rm-detail-badge', boolBadge(item.propsolarlease)]">{{ boolLabel(item.propsolarlease) }}</span>
              </div>
            </div>
          </div>

          <!-- Alternate Contact -->
          <div class="rm-detail-section" v-if="item.altcontactname">
            <div class="rm-detail-section-header">
              <i class="fa-solid fa-phone-volume"></i>
              <span>Alternate Contact</span>
            </div>
            <div class="rm-detail-grid">
              <div class="rm-detail-field">
                <span class="rm-detail-label">Name</span>
                <span class="rm-detail-value">{{ item.altcontactname || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Phone</span>
                <span class="rm-detail-value">{{ item.altcontactphone || '—' }}</span>
              </div>
              <div class="rm-detail-field">
                <span class="rm-detail-label">Relationship</span>
                <span class="rm-detail-value">{{ item.altcontactrelationship || '—' }}</span>
              </div>
              <div class="rm-detail-field rm-detail-field--full">
                <span class="rm-detail-label">Address</span>
                <span class="rm-detail-value">
                  {{ [item.altcontactstreet, item.altcontactcity, item.altcontactstate, item.altcontactzip].filter(Boolean).join(', ') || '—' }}
                </span>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Footer ── -->
        <div class="rm-detail-footer">
          <button class="rm-detail-footer-close" @click="$emit('close')">
            <i class="fa-solid fa-xmark"></i>&nbsp; Close
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ReverseMortgageDetailModal',

  props: {
    open: { type: Boolean, default: false },
    item: { type: Object, default: () => ({}) }
  },

  emits: ['close'],

  methods: {
    boolLabel(v) {
      if (v === true  || v === 1) return 'Yes'
      if (v === false || v === 0) return 'No'
      return '—'
    },
    boolBadge(v) {
      if (v === true  || v === 1) return 'badge--yes'
      if (v === false || v === 0) return 'badge--no'
      return 'badge--neutral'
    },
    // Full SSN (the API only returns it to people who can see this referral).
    formatSsn(ssn) {
      if (!ssn) return '—'
      const s = ssn.toString().replace(/\D/g, '')
      if (s.length === 9) return `${s.slice(0, 3)}-${s.slice(3, 5)}-${s.slice(5)}`
      return ssn
    },
    formatDob(dob) {
      if (!dob) return '—'
      try {
        const d = new Date(dob)
        if (isNaN(d.getTime())) return dob
        return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      } catch { return dob }
    }
  }
}
</script>

<style scoped>
/* ── Overlay ── */
.rm-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(10, 20, 40, 0.6);
  backdrop-filter: blur(4px);
}

/* ── Modal shell ── */
.rm-detail-modal {
  background: #f0f3f8;
  border-radius: 8px;
  width: 100%;
  max-width: 900px;
  max-height: 88vh;
  border: 1px solid #d4daea;
  box-shadow: 0 8px 32px rgba(10, 20, 40, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.rm-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f0f3f8;
  border-bottom: 1px solid #d4daea;
  flex-shrink: 0;
}

.rm-detail-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rm-detail-header-left > i {
  font-size: 2.2rem;
  color: #5a7fc4;
  flex-shrink: 0;
}

.rm-detail-title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  color: #28344f;
}

.rm-detail-subtitle {
  margin: 2px 0 0;
  font-size: 1.3rem;
  color: #6f7b92;
  font-weight: 500;
}

.rm-detail-close {
  width: 32px;
  height: 32px;
  border: 1px solid #c6cfe3;
  background: #dce2ef;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: #4a5675;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.rm-detail-close:hover {
  background: #fecaca;
  border-color: #f87171;
  color: #b91c1c;
}

/* ── Meta bar ── */
.rm-detail-meta-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 20px;
  padding: 8px 18px;
  background: #e8ecf4;
  border-bottom: 2px solid #c6cfe3;
  flex-shrink: 0;
}

.rm-detail-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #4a5675;
}

.rm-detail-meta-item i {
  color: #5a7fc4;
  font-size: 1.2rem;
}

/* ── Section card — matches cc-table-card ── */
.rm-detail-section {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  overflow: hidden;
}

/* ── Section header — matches cc-title-row ── */
.rm-detail-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: #f0f3f8;
  border-bottom: 1px solid #d4daea;
  font-size: 1.2rem;
  font-weight: 700;
  color: #6f7b92;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}

.rm-detail-section-header i {
  font-size: 1.3rem;
  color: #5a7fc4;
}

.rm-detail-section--no-cobwr .rm-detail-section-header {
  border-bottom: none;
}

.rm-detail-empty {
  padding: 12px 14px;
  font-size: 1.35rem;
  color: #7b8397;
  font-style: italic;
  margin: 0;
}

/* ── Grid — alternating rows like cc-row-even/odd ── */
.rm-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.rm-detail-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  border-bottom: 1px solid #edf0f5;
  border-right: 1px solid #edf0f5;
}

.rm-detail-field {
  background: #fbfcfe;
}

.rm-detail-field:hover {
  background: #eaf0fb !important;
}

.rm-detail-field:nth-child(3n) {
  border-right: none;
}

.rm-detail-field--full {
  grid-column: 1 / -1;
  border-right: none;
  background: #fbfcfe !important;
}

/* ── Labels — matches cc-table th ── */
.rm-detail-label {
  font-size: 1.05rem;
  font-weight: 700;
  color: #6f7b92;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1;
}

/* ── Values — matches cc-table td ── */
.rm-detail-value {
  font-size: 1.35rem;
  color: #23314f;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.rm-detail-value--cap { text-transform: capitalize; }

.rm-detail-value--ssn {
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 0.05em;
  color: #6f7b92;
}

/* ── Badges ── */
.rm-detail-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 1.1rem;
  font-weight: 700;
  width: fit-content;
  margin-top: 3px;
}

.badge--yes {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.badge--no {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.badge--neutral {
  background: #dce2ef;
  color: #6f7b92;
  border: 1px solid #c6cfe3;
}

/* ── Footer ── */
.rm-detail-footer {
  padding: 12px 18px;
  background: #fafbfd;
  border-top: 1px solid #e9edf3;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.rm-detail-footer-close {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  height: 34px;
  border-radius: 5px;
  border: 1px solid #d9dfeb;
  background: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  color: #22314f;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.rm-detail-footer-close:hover {
  background: #f4f7fc;
  border-color: #cfd8e8;
}

/* ── Transition ── */
.rm-modal-fade-enter-active,
.rm-modal-fade-leave-active { transition: opacity 0.18s ease; }

.rm-modal-fade-enter-active .rm-detail-modal,
.rm-modal-fade-leave-active .rm-detail-modal {
  transition: transform 0.2s cubic-bezier(0.34, 1.1, 0.64, 1), opacity 0.18s ease;
}

.rm-modal-fade-enter-from,
.rm-modal-fade-leave-to { opacity: 0; }

.rm-modal-fade-enter-from .rm-detail-modal,
.rm-modal-fade-leave-to .rm-detail-modal {
  transform: translateY(12px) scale(0.98);
  opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 700px) {
  .rm-detail-overlay { padding: 0; align-items: flex-end; }
  .rm-detail-modal { border-radius: 12px 12px 0 0; }
  .rm-detail-grid { grid-template-columns: repeat(2, 1fr); }
  .rm-detail-field:nth-child(3n) { border-right: 1px solid #edf0f5; }
  .rm-detail-field:nth-child(2n) { border-right: none; }
}
.rm-detail-body{
  overflow-y: auto;
}
</style>